/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useMemo, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp } from 'lucide-react';
import DepartmentFilter from './DepartmentFilter';
import { calculateDaysBetween } from '../utils/vacationUtils';

const getCurrentCycleYear = () => {
  const now = new Date();
  return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
};

const YEARS = [getCurrentCycleYear() - 1, getCurrentCycleYear(), getCurrentCycleYear() + 1];

const YearlyBalanceView = ({ annualBalances = [], departments = [], userDatabase = [], vacationRequests = [], holidays = [] }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedYears, setSelectedYears] = useState([getCurrentCycleYear()]);
  const [expandedCards, setExpandedCards] = useState([]);
  const [showOlderYears, setShowOlderYears] = useState(false);

  const visibleYears = showOlderYears
    ? [YEARS[0] - 2, YEARS[0] - 1, ...YEARS]
    : YEARS;

  const indexedRequests = useMemo(() => {
    const index = {};
    if (!vacationRequests) return index;

    vacationRequests.forEach(req => {
      if (req.status !== 'approved') return;
      
      const userId = Number(req.user_id || req.userId);
      if (!index[userId]) index[userId] = { granted: {}, approved: {} };

      const isGranted = req.manager_granted || req.managerGranted;
      const startDate = req.start_date || req.startDate;
      if (!startDate) return;

      const dateObj = new Date(startDate);
      const month = dateObj.getMonth();
      const year = dateObj.getFullYear();
      const cycleYear = month < 3 ? year - 1 : year;

      const days = calculateDaysBetween(startDate, req.end_date || req.endDate, holidays);

      if (isGranted) {
        // Granted (mandatory) days counted separately — but NOT granted-unpaid.
        // Manager-granted unpaid leave must never reduce the vacation balance,
        // so it is excluded here (mirrors TeamView / getUsedDays). Without this
        // guard, a granted-unpaid day fell into the granted bucket and was
        // subtracted from "Απομένουν".
        const grantedLeaveType = (req.leave_type || req.leaveType || '').toLowerCase();
        if (grantedLeaveType !== 'unpaid') {
          index[userId].granted[cycleYear] = (index[userId].granted[cycleYear] || 0) + days;
        }
      } else {
        const leaveType = (req.leave_type || req.leaveType || '').toLowerCase();
        if (leaveType === 'vacation') {
        index[userId].approved[cycleYear] = (index[userId].approved[cycleYear] || 0) + days;
  }
      }
    });

    return index;
  }, [vacationRequests, holidays]);

  const toggleYear = (year) => {
    setSelectedYears(prev =>
      prev.includes(year)
        ? prev.length === 1 ? prev : prev.filter(y => y !== year)
        : [...prev, year].sort()
    );
  };

  const toggleCard = (userId) => {
    setExpandedCards(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  useEffect(() => {
    if (departments.length > 0 && selectedDepartments.length === 0) {
      setSelectedDepartments(departments.map(d => d.id));
    }
  }, [departments]);

  const getAllowance = (userId, year) => {
    const pivoted = annualBalances.find(b => Number(b.userId || b.user_id) === Number(userId));
    if (pivoted) {
      const key = 'balance_' + year;
      if (pivoted[key] !== undefined) return Number(pivoted[key]);
    }
    const raw = annualBalances.find(b =>
      Number(b.user_id || b.userId) === Number(userId) &&
      Number(b.cycle_start_year || b.year) === year
    );
    return raw?.total_allowed !== undefined ? Number(raw.total_allowed) : 0;
  };

  const getApproved = (userId, year) => indexedRequests[userId]?.approved?.[year] || 0;
  const getGranted  = (userId, year) => indexedRequests[userId]?.granted?.[year]  || 0;

  const getOverflowFromPrevYear = (userId, year) => {
    const prevYear = year - 1;
    const prevAllowed = getAllowance(userId, prevYear);
    if (prevAllowed === 0) return 0;
    const prevRawApproved = getApproved(userId, prevYear);
    const prevGranted = getGranted(userId, prevYear);
    const prevOverflow = prevRawApproved + prevGranted - prevAllowed;
    return prevOverflow > 0 ? prevOverflow : 0;
  };

  const filteredData = useMemo(() => {
    return annualBalances.filter(item => {
      const name = item?.name || '';
      const matchesSearch = name.toLowerCase().includes(searchTerm.toLowerCase());
      const userId = item?.userId || item?.user_id;
      const userInDb = userDatabase?.find(u => u.id === userId);
      const deptId = item?.departmentId || item?.department_id || userInDb?.department_id || userInDb?.departmentId;
      const matchesDept = selectedDepartments.length === 0 || selectedDepartments.includes(Number(deptId));
      return matchesSearch && matchesDept;
    });
  }, [annualBalances, searchTerm, selectedDepartments, userDatabase]);

  const colorForRemaining = (remaining, allowed) => {
    if (allowed === 0) return 'text-gray-400 dark:text-jewel-text-muted';
    if (remaining <= 0) return 'text-red-600 dark:text-red-400 font-bold';
    if (remaining <= 5) return 'text-orange-500 dark:text-orange-400 font-bold';
    return 'text-green-600 dark:text-green-400 font-bold';
  };

  const sharedControls = (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white dark:bg-jewel-bg p-4 rounded-xl border border-gray-200 dark:border-jewel-border shadow-sm">
        <div className="relative flex-1 w-full min-w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-jewel-text-muted" />
          <input
            type="text"
            placeholder="Αναζήτηση υπαλλήλου..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-jewel-border bg-white dark:bg-jewel-surface text-gray-900 dark:text-jewel-text rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="w-full sm:w-auto">
          <DepartmentFilter
            departments={departments}
            selectedDepartments={selectedDepartments}
            onDepartmentChange={setSelectedDepartments}
          />
        </div>
      </div>
      <div className="mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-gray-600 dark:text-jewel-text">Χρονολογία:</span>
          {visibleYears.map(year => (
            <button
              key={year}
              onClick={() => toggleYear(year)}
              className={'px-4 py-1.5 rounded-full text-sm font-semibold border-2 transition-all ' +
                (selectedYears.includes(year)
                  ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white dark:bg-jewel-bg border-gray-300 dark:border-jewel-border text-gray-500 dark:text-jewel-text hover:border-blue-400')}
            >
              {year}
            </button>
          ))}
          <button
            onClick={() => setShowOlderYears(prev => !prev)}
            className="px-3 py-1.5 rounded-full text-xs font-semibold text-gray-400 dark:text-jewel-text-muted hover:text-gray-600 dark:hover:text-jewel-text underline transition-colors"
          >
            {showOlderYears ? 'Απόκρυψη παλαιότερων' : 'Παλαιότερα έτη'}
          </button>
        </div>
        <p className="text-xs text-gray-400 dark:text-jewel-text-muted mt-1">(1 Απριλίου – 31 Μαρτίου)</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {sharedControls}
      {/* MOBILE card list */}
      <div className="md:hidden space-y-3">
        {filteredData.length === 0 ? (
          <p className="text-center text-gray-400 dark:text-jewel-text-muted py-10 text-sm">
            {annualBalances.length === 0 ? 'Δεν υπάρχουν δεδομένα στη βάση.' : 'Δεν βρέθηκαν αποτελέσματα.'}
          </p>
        ) : filteredData.map(row => {
          const userId = row.userId || row.user_id;
          const isExpanded = expandedCards.includes(userId);
          
          const yearData = selectedYears.map(year => {
            const allowed = getAllowance(userId, year);
            const rawApproved = getApproved(userId, year);
            const approved = allowed > 0 ? Math.min(rawApproved, allowed) : rawApproved;
            const granted = getGranted(userId, year);
            const overflow = getOverflowFromPrevYear(userId, year);
            const remaining = allowed - approved - granted - overflow;
            const ownRemaining = allowed - rawApproved - granted;
            const overdrawn = ownRemaining < 0 && allowed > 0 ? Math.abs(ownRemaining) : 0;
            return { year, allowed, approved, granted, overflow, remaining, overdrawn };
          });

          const hasWarning = yearData.some(d => d.overdrawn > 0 || d.overflow > 0);
          const primary = yearData[yearData.length - 1];

          return (
            <div key={userId} className="bg-white dark:bg-jewel-bg border border-gray-200 dark:border-jewel-border rounded-xl shadow-sm overflow-hidden">
              <div className="flex items-center justify-between px-4 py-3 cursor-pointer select-none" onClick={() => toggleCard(userId)}>
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-semibold text-gray-800 dark:text-jewel-text text-sm truncate">{row.name}</span>
                  {hasWarning && <span className="text-orange-500 text-sm flex-shrink-0">⚠️</span>}
                </div>
                {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400 dark:text-jewel-text-muted flex-shrink-0" /> : <ChevronDown className="h-4 w-4 text-gray-400 dark:text-jewel-text-muted flex-shrink-0" />}
              </div>
              {!isExpanded && (
                <div className="px-4 pb-3 border-t border-gray-100 dark:border-jewel-border">
                  <p className="text-[10px] font-bold text-gray-400 dark:text-jewel-text-muted uppercase mt-2 mb-2">1 Απριλίου {primary.year} – 31 Μαρτίου {primary.year + 1}</p>
                  <div className="grid grid-cols-4 gap-1 text-center">
                    <div><p className="text-[10px] text-gray-400 dark:text-jewel-text-muted uppercase">Σύνολο</p><p className="text-sm font-bold text-gray-700 dark:text-jewel-text">{primary.allowed || '—'}</p></div>
                    <div><p className="text-[10px] text-blue-400 dark:text-jewel-teal-tint uppercase">Εγκρ.</p><p className="text-sm font-bold text-blue-600 dark:text-jewel-teal-tint">{(primary.approved + primary.overflow) > 0 ? primary.approved + primary.overflow : '—'}</p></div>
                    <div><p className="text-[10px] text-purple-400 dark:text-jewel-ruby-tint uppercase">Χορ.</p><p className="text-sm font-bold text-purple-600 dark:text-jewel-ruby-tint">{primary.granted > 0 ? primary.granted : '—'}</p></div>
                    <div><p className="text-[10px] text-green-500 dark:text-green-400 uppercase">Υπόλ.</p><p className={`text-sm ${colorForRemaining(primary.remaining, primary.allowed)}`}>{primary.allowed > 0 ? Math.max(0, primary.remaining) : '—'}</p></div>
                  </div>
                </div>
              )}
              {isExpanded && (
                <div className="divide-y divide-gray-100 dark:divide-jewel-border border-t border-gray-100 dark:border-jewel-border">
                  {yearData.map(({ year, allowed, approved, granted, overflow, remaining, overdrawn }) => (
                    <div key={year} className="px-4 py-3">
                      <p className="text-[10px] font-bold text-gray-500 dark:text-jewel-text-muted uppercase mb-2">1 Απριλίου {year} – 31 Μαρτίου {year + 1}</p>
                      <div className="space-y-1.5">
                        {[
                          { label: 'Σύνολο', value: allowed || '—', cls: 'text-gray-700 dark:text-jewel-text' },
                          { label: 'Εγκρίθηκαν', value: (approved + overflow) > 0 ? approved + overflow : '—', cls: 'text-blue-600 dark:text-jewel-teal-tint' },
                          { label: 'Χορηγήθηκαν', value: granted > 0 ? granted : '—', cls: 'text-purple-600 dark:text-jewel-ruby-tint' },
                          { label: 'Απομένουν', value: allowed > 0 ? Math.max(0, remaining) : '—', cls: colorForRemaining(remaining, allowed) },
                        ].map(({ label, value, cls }) => (
                          <div key={label} className="flex items-center justify-between text-sm">
                            <span className="text-gray-500 dark:text-jewel-text-muted">{label}</span>
                            <span className={`font-bold ${cls}`}>{value}</span>
                          </div>
                        ))}
                      </div>
                      {overdrawn > 0 && <p className="text-[10px] text-orange-600 dark:text-orange-400 mt-2 bg-orange-50 dark:bg-orange-900/30 rounded px-2 py-1">⚠️ Υπέρβαση {overdrawn} ημερών — χρεώθηκαν από {year + 1}</p>}
                      {overflow > 0 && <p className="text-[10px] text-red-600 dark:text-red-400 mt-1 bg-red-50 dark:bg-red-900/30 rounded px-2 py-1">⚠️ {overflow} ημέρες δανεισμένες από {year - 1}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* DESKTOP Table */}
      <div className="hidden md:block bg-white dark:bg-jewel-bg rounded-xl border border-gray-200 dark:border-jewel-border shadow-sm overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-jewel-border">
          <thead>
            <tr className="bg-gray-50 dark:bg-jewel-bg">
              <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 dark:text-jewel-text-muted uppercase" rowSpan="2">Υπάλληλος</th>
              {selectedYears.map(year => (
                <th key={year} colSpan="4" className="px-2 py-2 text-center text-xs font-bold text-gray-700 dark:text-jewel-text uppercase border-l border-gray-200 dark:border-jewel-border">
                  {'1 Απριλίου ' + year + ' – 31 Μαρτίου ' + (year + 1)}
                </th>
              ))}
            </tr>
            <tr className="bg-gray-50 dark:bg-jewel-bg border-t border-gray-200 dark:border-jewel-border">
              {selectedYears.map(year => (
                <React.Fragment key={year}>
                  <th className="px-2 py-2 text-center text-[10px] font-semibold text-gray-400 dark:text-jewel-text-muted uppercase border-l border-gray-200 dark:border-jewel-border">Σύνολο</th>
                  <th className="px-2 py-2 text-center text-[10px] font-semibold text-blue-400 dark:text-jewel-teal-tint uppercase">Εγκρίθηκαν</th>
                  <th className="px-2 py-2 text-center text-[10px] font-semibold text-purple-400 dark:text-jewel-ruby-tint uppercase">Χορηγήθηκαν</th>
                  <th className="px-2 py-2 text-center text-[10px] font-semibold text-green-500 dark:text-green-400 uppercase">Απομένουν</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-jewel-border">
            {filteredData.map(row => {
              const userId = row.userId || row.user_id;
              const userGender = userDatabase?.find(u => Number(u.id) === Number(userId))?.gender;
              const article = userGender === 'female' ? 'Η' : 'Ο';
              let overflowTooltip = null;

              return (
                <tr key={userId} className="hover:bg-gray-50 dark:hover:bg-jewel-surface/50">
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-jewel-text border-r border-gray-200 dark:border-jewel-border text-sm whitespace-nowrap">{row.name}</td>
                  {selectedYears.map(year => {
                    const allowed = getAllowance(userId, year);
                    const rawApproved = getApproved(userId, year);
                    const approved = allowed > 0 ? Math.min(rawApproved, allowed) : rawApproved;
                    const granted = getGranted(userId, year);
                    const overflow = getOverflowFromPrevYear(userId, year);
                    const remaining = allowed - approved - granted - overflow;
                    const ownRemaining = allowed - rawApproved - granted;

                    if (ownRemaining < 0 && allowed > 0 && !overflowTooltip) {
                      overflowTooltip = (
                        <span>
                          {article} {row.name} χρησιμοποίησε επιπλέον{' '}
                          <span className="text-red-400 dark:text-red-300 font-bold">{Math.abs(ownRemaining)} ημέρες</span>{' '}
                          από το {year + 1}.
                        </span>
                      );
                    }

                    return (
                      <React.Fragment key={year}>
                        <td className="px-2 py-3 text-center text-sm font-semibold text-gray-600 dark:text-jewel-text border-l border-gray-100 dark:border-jewel-border">{allowed || '—'}</td>
                        <td className="px-2 py-3 text-center text-sm font-semibold text-blue-600 dark:text-jewel-teal-tint">{(approved + overflow) > 0 ? (approved + overflow) : '—'}</td>
                        <td className="px-2 py-3 text-center text-sm font-semibold text-purple-600 dark:text-jewel-ruby-tint">{granted > 0 ? granted : '—'}</td>
                        <td className={'px-2 py-3 text-center text-sm ' + colorForRemaining(remaining, allowed)}>{allowed > 0 ? Math.max(0, remaining) : '—'}</td>
                      </React.Fragment>
                    );
                  })}
                  {overflowTooltip && (
                    <td className="px-2 py-3 text-center">
                      <div className="relative group inline-block">
                        <span className="text-orange-500 cursor-help text-base">⚠️</span>
                        <div className="absolute right-0 bottom-full mb-2 w-72 bg-gray-900 dark:bg-jewel-surface text-white text-xs rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 text-left shadow-xl">
                          {overflowTooltip}
                          <div className="absolute top-full right-4 border-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                        </div>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default YearlyBalanceView;